import { addTypenameToDocument } from "@apollo/client/utilities";
import { type GraphQLError, parse } from "graphql";
import { request } from "graphql-request";

import appConfig from "config/app";
import { APOLLO_ERROR_CODES } from "src/constants";
import { useAuthStore } from "src/stores/auth-store";
import { logger, maskSensitiveData, stringify } from "src/utils";

import { ensureValidToken, handleUnauthenticatedError } from "./auth-handler";
import { PlatoErrorCodesEnum } from "./generated/types";

interface ErrorResponse {
  message: string;
  path: Array<string>;
  extensions: {
    [key: string]: any;
  };
}

export interface ServerError {
  response?: {
    errors?: Array<GraphQLError>;
  };
  request?: {
    query?: string;
    variables?: {
      [key: string]: unknown;
    };
  };
}

interface GraphQLRequestError {
  response: {
    errors: Array<ErrorResponse>;
  };
  request: {
    [key: string]: unknown;
  };
}

const LOG_PREFIX = "[Fetcher]";

/**
 * Process errors and determine how to handle them
 * Returns true if the error was handled and request should be retried
 */
const processErrors = async <TData>(
  errors: Array<ErrorResponse>,
  executeRequest: () => Promise<TData>
): Promise<TData | null> => {
  for (const error of errors) {
    const { message, path, extensions } = error;
    const isUpdatePasswordPath = path && path[0] === "updatePassword";

    // Handle UNAUTHENTICATED errors (except for updatePassword)
    if (extensions?.code === APOLLO_ERROR_CODES.UNAUTHENTICATED && !isUpdatePasswordPath) {
      // Don't logout immediately - try refresh first
      return handleUnauthenticatedError(executeRequest, LOG_PREFIX);
    }

    // Don't log certain expected errors
    const isLoginAlreadyChanged = extensions?.errorCode === PlatoErrorCodesEnum.LoginAlreadyChanged;
    const isUpdatePasswordUnauth =
      extensions?.code === APOLLO_ERROR_CODES.UNAUTHENTICATED && isUpdatePasswordPath;

    if (!isLoginAlreadyChanged && !isUpdatePasswordUnauth) {
      const statusCode = typeof extensions?.status === "number" ? extensions.status : undefined;
      logger.error(
        `[GraphQL error]: Message: ${message}, Path: ${path}, Extensions: ${stringify(extensions)}`,
        statusCode !== undefined ? { http: { status_code: statusCode } } : undefined
      );
    }
  }

  return null;
};

export const fetcher = <TData, TVariables extends Record<string, unknown>>(
  query: string,
  variables?: TVariables
): (() => Promise<TData>) => {
  return async () => {
    // Ensure token is valid before making the request
    await ensureValidToken(LOG_PREFIX);

    // Adds "__typename" to the query
    const document = addTypenameToDocument(parse(query));

    const executeRequest = async (): Promise<TData> => {
      // Get fresh token in case it was refreshed
      const currentState = useAuthStore.getState();
      const currentToken = currentState.token;

      const headers = {
        authorization: currentToken ? `Bearer ${currentToken}` : "",
      };

      logger.info(
        `query ${query} start with variables: ${JSON.stringify(maskSensitiveData(variables))}`
      );

      if (appConfig.graphql.url) {
        const data = await request<TData>(
          `${appConfig.graphql.url}/graphql`,
          document,
          variables,
          headers
        );

        logger.info(`query ${query} end with: ${JSON.stringify(data)}`);

        return data;
      }

      throw new Error("GraphQL URL not configured");
    };

    try {
      return await executeRequest();
    } catch (err) {
      const error = err as GraphQLRequestError;

      if (error?.response?.errors) {
        // Try to handle errors (may retry with refreshed token)
        const retryResult = await processErrors(error.response.errors, executeRequest);
        if (retryResult !== null) {
          return retryResult;
        }
      }

      throw error;
    }
  };
};
